import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, IconButton, Chip, CircularProgress, Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Close, AccountBalanceWalletOutlined, LocalCafeOutlined, LockOutlined, AccountBalanceOutlined, MoveToInbox,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useGetCashOfficeSummaryQuery } from '../../../services/cashOfficeApi';
import { useLazyGetSessionQuery } from '../../../services/cashSessionApi';
import { setCurrentSession } from '../../../store/cashSessionSlice';
import { useToast } from '../../common/ToastProvider';
import { P, money, Note, Label } from './cashOfficeShared';
import { ExpenseForm, DrawerForm, ReceiveForm } from './CashOfficeForms';

const ACTIONS = [
  { key: 'expense', icon: LocalCafeOutlined, label: 'Petty Expense', short: 'Expense' },
  { key: 'drop', icon: LockOutlined, label: 'Safe Drop', short: 'Safe drop' },
  { key: 'topup', icon: AccountBalanceWalletOutlined, label: 'Top Up Petty', short: 'Top up' },
  { key: 'deposit', icon: AccountBalanceOutlined, label: 'Bank Deposit', short: 'Deposit' },
  { key: 'receive', icon: MoveToInbox, label: 'Receive Cash', short: 'Receive' },
];
const DRAWER_ACTIONS = ['drop', 'topup', 'deposit'];

function SummaryTiles({ drawer, petty, incoming }) {
  const tiles = [['DRAWER', drawer === null ? '—' : money(drawer)], ['PETTY CASH', petty === null ? '—' : money(petty)], ['INCOMING', String(incoming)]];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: { xs: 0.75, sm: 1 } }}>
      {tiles.map(([label, value]) => (
        <Box key={label} sx={{ bgcolor: P.surface, border: `1px solid ${P.border}`, borderRadius: '8px', px: { xs: 1, sm: 1.25 }, py: { xs: 0.75, sm: 1 }, minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: '0.52rem', sm: '0.58rem' }, fontWeight: 800, color: P.muted, letterSpacing: '0.04em' }}>{label}</Typography>
          <Typography noWrap sx={{ fontSize: { xs: '0.78rem', sm: '0.92rem' }, fontWeight: 800, color: P.text, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function ActionPicker({ value, onChange, noSession }) {
  return (
    <Box>
      <Label>What do you need to do?</Label>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: { xs: 0.6, sm: 0.75 } }}>
        {ACTIONS.map(({ key, icon, label, short }) => {
          const on = value === key;
          const needsSession = noSession && DRAWER_ACTIONS.includes(key);
          return (
            <Button key={key} onClick={() => onChange(key)} aria-pressed={on} title={needsSession ? 'Open a cash session first' : label}
              sx={{
                height: { xs: 56, sm: 58 }, minWidth: 0, p: 0.5, borderRadius: '8px', border: '1px solid', textTransform: 'none',
                display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: { xs: '0.6rem', sm: '0.68rem' }, fontWeight: 700, lineHeight: 1.15,
                ...(on ? { bgcolor: P.brand, borderColor: P.brand, color: '#fff', '&:hover': { bgcolor: P.brandHover } }
                  : { bgcolor: '#fff', borderColor: P.border, color: needsSession ? P.disabled : P.secondary, '&:hover': { bgcolor: P.surface } }),
              }}>
              {React.createElement(icon, { sx: { fontSize: 18 } })}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>{label}</Box>
              <Box component="span" sx={{ display: { xs: 'block', sm: 'none' } }}>{short}</Box>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

// Store Cash Office at the counter: petty expense, safe drop, top up petty, bank deposit, receive cash sent here.
// Drawer actions write a Cash Out on the open session (Cash Office system reasons), so expected cash stays right.
const CashOfficeModal = ({ open, onClose, session, readOnly }) => {
  const dispatch = useDispatch();
  const showToast = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const storeId = useSelector((s) => s.auth?.store?.id);
  const [act, setAct] = useState('expense');
  const [done, setDone] = useState(null);
  const { data, isLoading, isError, error, refetch } = useGetCashOfficeSummaryQuery(undefined, { skip: !open });
  const [triggerGetSession] = useLazyGetSessionQuery();
  const summary = data?.data;

  // Fresh expected cash for the drawer each time the panel opens.
  const sessionId = session?.id;
  const refreshSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await triggerGetSession(sessionId).unwrap();
      if (res?.data?.session) dispatch(setCurrentSession(res.data.session));
    } catch {
      // Non-critical: the tile shows the last known figure.
    }
  }, [sessionId, triggerGetSession, dispatch]);
  useEffect(() => {
    if (open) refreshSession();
  }, [open, refreshSession]);
  // Start on Petty Expense, without a receipt, next time it opens.
  const close = () => {
    onClose();
    setAct('expense');
    setDone(null);
  };

  const drawerExpected = session?.summary?.expected_cash != null ? Number(session.summary.expected_cash) : null;
  const petty = (summary?.cash_points || []).filter((p) => p.kind === 'petty_fund');
  const pettyAvailable = petty.length ? petty.reduce((s, p) => s + Number(p.available), 0) : null;
  const noSession = !session || session.status !== 'open';

  const handleDone = async (result) => {
    if (result.drawerChanged) await refreshSession();
    (result.warnings || []).forEach((w) => showToast(w, 'warning'));
    showToast(`${result.number} recorded`, 'success');
    setDone(result);
  };

  const subtitle = [session?.session_number, summary?.store?.name].filter(Boolean).join(' · ');
  const top = (
    <>
      {readOnly && <Note tone="wa">Cash Office was switched off for your organization. You can look, but not record.</Note>}
      <SummaryTiles drawer={drawerExpected} petty={pettyAvailable} incoming={summary?.incoming_transfers ?? 0} />
      <ActionPicker value={act} onChange={setAct} noSession={noSession} />
    </>
  );

  let content;
  if (isLoading) {
    content = <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={30} sx={{ color: P.brand }} /></DialogContent>;
  } else if (isError) {
    const forbidden = error?.status === 403;
    content = (
      <DialogContent sx={{ textAlign: 'center', py: 5 }}>
        <Typography sx={{ fontSize: '0.85rem', color: P.text2, mb: 1.5 }}>
          {forbidden ? error?.data?.error || "You don't have access to Cash Office at this store." : "Couldn't load Cash Office."}
        </Typography>
        {!forbidden && <Button onClick={refetch} sx={{ textTransform: 'none', fontWeight: 700, color: P.brand }}>Retry</Button>}
      </DialogContent>
    );
  } else if (done) {
    content = (
      <>
        <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
          <Box sx={{ border: `1px dashed ${P.disabled}`, borderRadius: '10px', p: 2, display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
            <Chip label="Done" size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, borderRadius: '6px', bgcolor: P.successBg, color: P.success }} />
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1rem', color: P.text }}>{done.number}</Typography>
            <Typography sx={{ fontSize: '0.85rem', color: P.text }}>{done.text}</Typography>
            {done.drawerChanged && drawerExpected !== null && (
              <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>Drawer expected cash is now {money(drawerExpected)}.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pt: 1, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={close}
            sx={{ flex: 1, py: 1.1, fontWeight: 600, fontSize: '0.85rem', borderRadius: '8px', textTransform: 'none', borderColor: P.border, color: P.text2 }}>
            Close
          </Button>
          <Button variant="contained" disableElevation onClick={() => setDone(null)}
            sx={{ flex: 2, py: 1.1, fontWeight: 700, fontSize: '0.85rem', borderRadius: '8px', textTransform: 'none', bgcolor: P.brand, '&:hover': { bgcolor: P.brandHover } }}>
            Another Entry
          </Button>
        </DialogActions>
      </>
    );
  } else {
    const common = { top, summary, session, readOnly, onClose: close, onDone: handleDone };
    content = act === 'expense' ? <ExpenseForm {...common} />
      : act === 'receive' ? <ReceiveForm {...common} storeId={storeId ?? summary?.store?.id} />
        : <DrawerForm key={act} kind={act} drawerExpected={drawerExpected} {...common} />;
  }

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth={false} fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', width: isMobile ? '100%' : 560, m: isMobile ? 0 : 2 } }}>
      <DialogTitle sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${P.divider}` }}>
        <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: P.brandBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <AccountBalanceWalletOutlined sx={{ fontSize: 19, color: P.brand }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize="1rem" color={P.text} lineHeight={1.3}>Cash Office</Typography>
          {subtitle && <Typography fontSize="0.7rem" color={P.muted} fontWeight={500} noWrap>{subtitle}</Typography>}
        </Box>
        <IconButton size="small" onClick={close} aria-label="Close" sx={{ color: P.muted, mr: -0.5 }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Stack sx={{ flex: 1, minHeight: 0 }}>{content}</Stack>
    </Dialog>
  );
};

export default CashOfficeModal;
